/* RESPONSIBLE TEAM: team-reporting */

import type {
  ChartSchema,
  ChartDefinition,
  Timezone,
} from '@/features/report-snapshots/api/schema';
import DefaultDonutChartConfig from '@/features/report-snapshots/components/charts/default-donut-chart-config';
import { getSeries } from '@/features/report-snapshots/components/charts/utils/chart-data';
import { Chart, Credits, Legend, Tooltip } from '@highcharts/react';
import { Pie } from '@highcharts/react/series';
import { SERIES_COLOR_PALETTE } from '@/features/report-snapshots/constants';
import { I18n } from '@lingui/core';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';

interface DonutChartProps {
  chartSchema: ChartSchema;
  chartDefinition: ChartDefinition;
  timezone: Timezone;
  i18n: I18n;
}

export const DonutChart = ({ chartSchema, chartDefinition, timezone, i18n }: DonutChartProps) => {
  const config = new DefaultDonutChartConfig();
  const series = getSeries(chartSchema, 'pie', chartDefinition, timezone, i18n);

  config.setColors(SERIES_COLOR_PALETTE);

  const showDataLabels = chartDefinition.chartSeries.some(
    (chartSeries) => chartSeries.showDataLabels,
  );

  if (showDataLabels) {
    config.setDataLabels();
  }

  if (chartDefinition.viewBy === 'time') {
    config.setReversedLegend();
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ChartTitle chartSchema={chartSchema} chartDefinition={chartDefinition} />
      <div className="flex-1 min-h-0">
        <Chart
          options={{
            plotOptions: { ...config.config.plotOptions },
            colors: config.config.colors,
            lang: config.config.lang,
            accessibility: config.config.accessibility,
          }}
        >
          <Pie.Series data={series} type="pie" />
          <Tooltip
            hideDelay={config.config.tooltip?.hideDelay}
            snap={config.config.tooltip?.snap}
            borderRadius={config.config.tooltip?.borderRadius}
            borderWidth={config.config.tooltip?.borderWidth}
            borderColor={config.config.tooltip?.borderColor}
            shared={config.config.tooltip?.shared}
            format={config.config.tooltip?.format}
          />
          <Legend
            enabled={config.config.legend?.enabled}
            align={config.config.legend?.align}
            margin={config.config.legend?.margin}
            layout={config.config.legend?.layout}
            itemHoverStyle={config.config.legend?.itemHoverStyle}
            itemStyle={config.config.legend?.itemStyle}
            reversed={config.config.legend?.reversed}
          />
          <Credits enabled={false} />
        </Chart>
      </div>
    </div>
  );
};
